import React, { useEffect, useRef, useState } from 'react'
import { setAccessToken, clearAccessTokens } from 'api/accessTokenMgmt'
import { Link, useNavigate } from 'react-router-dom'
import HeroImage from 'components/auth/HeroImage.jsx'
import ReliableButton from 'components/shared/ReliableButton'
import flashStyles from 'components/shared/flash.module.scss'
import styles from 'components/auth/auth.module.scss'
import { isSafeRelativePath, navigateAfterAuth } from './postAuthNavigation'
import cn from 'classnames'
import { ChevronRight, Eye, EyeOff } from 'lucide-react'
import { rememberAccountReviewAuthHint } from '../../bootstrap/accountReviewSession'
import WalletRecovery from './WalletRecovery'
import OAuthButtons, { oauthErrorMessage } from './OAuthButtons'
import PasskeySignInButton from './PasskeySignInButton'

export default function Login({
    embedded = false,
    analyticsSource = undefined,
    onSwitchMode = undefined,
    onAuthenticated = undefined,
}) {
    const [success, setSuccess] = useState(/** @type {boolean | undefined} */ (undefined))
    const [message, setMessage] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [recoverWallet, setRecoverWallet] = useState(false)
    const navigate = useNavigate()

    const urlParams = new URLSearchParams(window.location.search)
    const authQueryKeys = [
        'space_invite',
        'stream_invite',
        'prospect_token',
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
        'gclid',
        'gbraid',
        'wbraid',
        'landing_page',
        'landing_button',
        'landing_page_url',
        'landing_page_host',
        'landing_page_path',
        'landing_page_search',
        'landing_referrer',
        'amplitude_device_id',
    ]

    const buildSignupQuery = extraParams => {
        const searchParams = new URLSearchParams()

        authQueryKeys.forEach(key => {
            const value = urlParams.get(key)
            if (value) searchParams.set(key, value)
        })

        Object.entries(extraParams || {}).forEach(([key, value]) => {
            if (value) searchParams.set(key, value)
        })

        const query = searchParams.toString()
        return query ? `?${query}` : ''
    }

    const signupQuery = buildSignupQuery()

    // Back from Google / X: redeem the one-time code for token headers.
    const oauthHandled = useRef(false)
    useEffect(() => {
        if (oauthHandled.current) return
        oauthHandled.current = true

        const oauthCode = urlParams.get('oauth_code')
        const oauthError = urlParams.get('oauth_error')
        if (!oauthCode && !oauthError) return

        const oauthNext = urlParams.get('oauth_next')
        if (oauthCode && isSafeRelativePath(oauthNext)) {
            try {
                sessionStorage.setItem('attempted_path', oauthNext)
            } catch (_error) {
                // sessionStorage unavailable; fall back to the default destination.
            }
        }
        window.history.replaceState(null, '', window.location.pathname)

        if (oauthError) {
            setSuccess(false)
            setMessage(oauthErrorMessage(oauthError))
            return
        }

        fetch(`${window.knovApiUrl}/oauth/session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ code: oauthCode }),
        })
            .then(async response => {
                const data = await response.json().catch(() => null)
                if (response.ok) {
                    authUser(extractTokenHeaders(response), data?.data)
                } else {
                    setSuccess(false)
                    setMessage(data?.errors?.[0] || oauthErrorMessage())
                }
            })
            .catch(() => {
                setSuccess(false)
                setMessage(oauthErrorMessage())
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    async function onSubmit(ev) {
        ev.preventDefault()

        window.logEv('CLICK_AUTH_LOGIN_SUBMIT', {
            auth_mode: 'login',
            auth_surface: embedded ? 'modal' : 'page',
            trigger_source: analyticsSource || 'direct_login',
        })

        const email = ev?.target?.elements?.['sign-in-email-field']?.value
        const password = ev?.target?.elements?.['sign-in-pass-field']?.value

        const response = await fetch(`${window.knovApiUrl}/auth/sign_in`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({ email, password }),
        })

        const data = await response.json().catch(() => null)
        if (response.ok) {
            const tokenHeaders = extractTokenHeaders(response)
            authUser(tokenHeaders, data?.data)
        } else {
            const errorMessage = data?.errors?.[0] || 'Authentication error.'
            setSuccess(false)
            setMessage(errorMessage)
        }
    }

    // extract token from response headers for token auth
    function extractTokenHeaders(response) {
        return {
            'access-token': response.headers.get('access-token'),
            expiry: response.headers.get('expiry'),
            uid: response.headers.get('uid'),
            client: response.headers.get('client'),
        }
    }

    // authenticate user with token headers
    function authUser(tokenHeaders, user) {
        clearAccessTokens()
        setAccessToken(tokenHeaders)
        rememberAccountReviewAuthHint(window, tokenHeaders, user)
        setSuccess(true)
        setMessage('Successfully logged in.')
        onAuthenticated?.()
        navigateAfterAuth({ embedded, navigate, fallbackPath: '/' })
    }

    const handleSwitchToSignup = event => {
        window.logEv('CLICK_AUTH_SWITCH_TO_SIGNUP', {
            auth_mode: 'login',
            auth_surface: embedded ? 'modal' : 'page',
            trigger_source: analyticsSource || 'direct_login',
        })

        if (onSwitchMode) {
            event.preventDefault()
            onSwitchMode()
            return
        }

        navigate(`/signup${signupQuery}`)
    }

    const formContent = (
        <div
            className={cn(
                'sign-in-form',
                styles.authSurface,
                !embedded && 'col-md-offset-2 col-md-8',
                embedded && styles.embeddedForm,
            )}
            data-test-id="sign-in"
        >
            <div className={styles.statusContainer}>
                <div
                    className={cn(
                        styles.errorMessage,
                        success === true && flashStyles.success,
                        success === false && flashStyles.error,
                    )}
                >
                    {message}
                </div>
            </div>

            {recoverWallet ? (
                <WalletRecovery
                    onBack={() => setRecoverWallet(false)}
                    onRecovered={(response, user) => authUser(extractTokenHeaders(response), user)}
                />
            ) : (
                <form id="new-session" onSubmit={onSubmit}>
                    <PasskeySignInButton
                        onSignedIn={(tokenHeaders, user) => {
                            window.logEv('AUTH_PASSKEY_SIGN_IN', {
                                auth_surface: embedded ? 'modal' : 'page',
                                trigger_source: analyticsSource || 'direct_login',
                            })
                            authUser(tokenHeaders, user)
                        }}
                        onError={errorMessage => {
                            setSuccess(false)
                            setMessage(errorMessage)
                        }}
                    />
                    <OAuthButtons
                        mode="login"
                        embedded={embedded}
                        onStart={provider =>
                            window.logEv('CLICK_AUTH_OAUTH', {
                                provider,
                                auth_mode: 'login',
                                auth_surface: embedded ? 'modal' : 'page',
                                trigger_source: analyticsSource || 'direct_login',
                            })
                        }
                    />
                    <div className={styles.authFields}>
                        <div className={cn('field', styles.fieldContainer)}>
                            <span className={cn('field-label', styles.fieldLabel)}>Email</span>
                            <input
                                style={{
                                    fontSize: /* this prevents autozoom on mobile */ '16px',
                                }}
                                type="email"
                                placeholder=""
                                className={cn('form-control', styles.inputField)}
                                autoComplete="email"
                                autoFocus={true}
                                id="sign-in-email-field"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div className={cn('field', styles.fieldContainer)}>
                            <span className={cn('field-label', styles.fieldLabel)}>Password</span>
                            <div className={styles.passwordField}>
                                <input
                                    style={{
                                        fontSize: /* this prevents autozoom on mobile */ '16px',
                                    }}
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder=""
                                    className={cn(
                                        'form-control',
                                        styles.inputField,
                                        styles.passwordInput,
                                    )}
                                    autoComplete="current-password"
                                    id="sign-in-pass-field"
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    className={styles.passwordToggle}
                                    onClick={() => setShowPassword(currentValue => !currentValue)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>

                            <div className={cn('auth-links', styles.forgotPassword)}>
                                <Link
                                    className={cn('devise-shared-signup', styles.textLink)}
                                    to="/auth/password/request-reset"
                                >
                                    Forgot Password?
                                </Link>
                            </div>
                        </div>
                        <div className={styles.signupContainer}>
                            <ReliableButton
                                type="submit"
                                className={cn(styles.loginButton)}
                                data-testid="login-submit-button"
                            >
                                Log In
                            </ReliableButton>
                        </div>
                    </div>

                    <div
                        className={cn(
                            'auth-links',
                            styles.signupBtnContainer,
                            styles.loginContainer,
                        )}
                    >
                        <div className={styles.loginComp}>
                            {"Don't have an account?"}

                            <Link
                                className={cn(
                                    'devise-shared-signup',
                                    styles.linkHover,
                                    styles.textLink,
                                )}
                                to={`/signup${signupQuery}`}
                                data-testid="login-signup-link"
                                onClick={handleSwitchToSignup}
                            >
                                {' '}
                                Join{' '}
                            </Link>
                        </div>
                    </div>
                    <div className={styles.recoveryEntry}>
                        <button
                            type="button"
                            className={styles.recoveryEntryButton}
                            onClick={() => {
                                setMessage('')
                                setRecoverWallet(true)
                            }}
                        >
                            <img src="/yours512.png" alt="" className={styles.recoveryEntryLogo} />
                            <span className={styles.recoveryEntryText}>
                                <span className={styles.recoveryEntryTitle}>
                                    Signed up with Yours Wallet?
                                </span>
                                <span className={styles.recoveryEntrySub}>
                                    Recover an account created with Yours
                                </span>
                            </span>
                            <ChevronRight
                                size={16}
                                aria-hidden="true"
                                className={styles.recoveryEntryChevron}
                            />
                        </button>
                    </div>
                </form>
            )}
        </div>
    )

    if (embedded) {
        return formContent
    }

    return (
        <div className="sessions_new landing">
            <div className="earth container-fluid">
                <div className="row">
                    <HeroImage />
                </div>
                {formContent}
            </div>
        </div>
    )
}
